import React, { useState, useContext, useEffect } from 'react';
import { Record } from 'immutable';
import { useSelector } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentlyTypingParticipants } from '../../hooks/useCurrentlyTypingParticpants';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';

// Context
import WorkspaceActionCableContext from 'Shared/contexts/WorkspaceActionCableContext';

// Utils
import { isEmpty, createRichTextEditorState } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Types
import { WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Helpers
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';
import * as UserHelper from 'Shared/model_helpers/User.helpers';

// Actions
// import * as workspaceItemCommentActions from 'Shared/actions/WorkspaceItemComments.actions';

// Selectors
import * as meetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';
// import * as workspaceItemCommentsSelectors from 'Shared/selectors/WorkspaceItemComments.selectors';

// Components
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from 'Wpds/components/tooltip';
import WpIcon from 'Wpds/components/icon/WpIcon';
import MeetingItemCommentsInput from 'Shared/components/MeetingItemDetailPanel/comments/MeetingItemCommentsInput';
import TypingDots from 'Shared/components/TypingDots/TypingDots';
import MeetingItemComments from 'Shared/components/MeetingItemDetailPanel/comments/MeetingItemComments';

// Styles
import './AddNote.scss';

type Props = {
  meetingModeSession: Record<WorkspaceMeetingMode>;
  onChange: Function;
};

const AddNote = ({ meetingModeSession, onChange }: Props) => {
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [editorState, setEditorState] = useState();

  const { isXtraLarge } = useResponsiveScreenWidth({
    isAtLeast: true,
  });

  const isTypingComment = !isEmpty(editorState);

  const workspaceChannel = useContext(WorkspaceActionCableContext);

  const typingParticipants = useCurrentlyTypingParticipants(meetingModeSession);
  const hasTypingParticipants = typingParticipants.size > 0;

  const meetingModeSessionId = WorkspaceMeetingModeHelper.getId(meetingModeSession);
  const meetingItemUuid = WorkspaceMeetingModeHelper.getWorkspaceMeetingItemUuid(meetingModeSession);

  const meetingItem = useSelector(state =>
    meetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid: meetingItemUuid })
  ).get(meetingItemUuid);

  const handleOnCreateItemSucess = () => {
    setEditorState(createRichTextEditorState(''));
  };

  const handleOnChange = value => {
    onChange(value);
    setEditorState(value);
  };

  useEffect(() => {
    let timer;
    const isTyping = isTypingComment;
    const updateChannel = () => {
      workspaceChannel.send({ event: 'user_typing', isTyping, meetingModeSessionId });

      if (isTyping) timer = setTimeout(updateChannel, 2000);
    };

    updateChannel();

    return () => clearTimeout(timer);
  }, [isTypingComment, meetingModeSessionId, workspaceChannel]);

  const baseClassName = 'AddNote';

  let typeText = 'someone is typing';
  if (typingParticipants.size === 1 && !UserHelper.getFirstName(typingParticipants.first()).includes('@')) {
    typeText = `${UserHelper.getFirstName(typingParticipants.first())} is typing`;
  }
  if (typingParticipants.size > 1) {
    typeText = `${typingParticipants.size} typing`;
  }

  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header`}>
          {!isXtraLarge ? 'Notes' : 'Add Note'}
          {hasTypingParticipants && (
            <WpTooltip>
              <WpTooltipTrigger>
                <div className={`${baseClassName}__header__typing-note`}>
                  <div className={`${baseClassName}__header__typing-note__dots`}>
                    <TypingDots />
                  </div>
                  {typeText}
                </div>
              </WpTooltipTrigger>
              <WpTooltipContent>
                {typingParticipants.valueSeq().map(user => {
                  return <div>{UserHelper.getFullName(user)}</div>;
                })}
              </WpTooltipContent>
            </WpTooltip>
          )}
        </div>
      </WpContainerHeader>
      <WpContainerBody>
        <div className={`${baseClassName}__body`}>
          {!isXtraLarge && (
            <MeetingItemComments
              meetingItem={meetingItem}
              className={`${baseClassName}__body__notes__list`}
              truncateLimit={2}
              emptyStateComponent={
                <div className={`${baseClassName}__body__notes__empty`}>There are no previous notes for this topic</div>
              }
            />
          )}
          {isOpen || !isXtraLarge || !isEmpty(editorState) ? (
            <MeetingItemCommentsInput
              meetingItem={meetingItem}
              enterToSend={false}
              simpleInput={isXtraLarge}
              showHint={false}
              showSaveButton
              hideVideo
              meetingModeSessionId={meetingModeSessionId}
              onChange={handleOnChange}
              editorState={editorState}
              placeholder="Leave a note..."
              onCreateSucess={handleOnCreateItemSucess}
              // if autofocus is true, the app crashes in firefox if another Rich text input is currently focused
              // aka - edit comment
              autoFocus={false}
            />
          ) : (
            <div className={`${baseClassName}__empty`}>
              <WpButton variant="tertiary" onClick={toggleIsOpen}>
                <WpIcon name="plus-circle" />
                Add Note
              </WpButton>
            </div>
          )}
        </div>
      </WpContainerBody>
    </WpContainer>
  );
};

export default AddNote;
